import { createAction, isRejected } from "@reduxjs/toolkit";
import { captureException } from "@sentry/react";

import { __GLOBAL_BROWSER_STORAGE } from "@/client/globals";
import { AppStartListening } from "@/data/listenerMiddleware";
import { onFocus } from "@/data/setup-dom-listeners";
import { AppDispatch, RootState } from "@/data/store";
import {
  isRetryableSaveError,
  PendingSnapshot,
  saveBlockSnapshot,
  SaveError,
  saveFull,
  SavingMechanism,
  selectPendingSnapshotsCount,
  selectSavesInFlightCount,
  selectSavingMechanism,
  setDocumentError,
  setPendingSnapshot,
} from "@/features/authority";
import { ClassicEditorName } from "@/stores/editor-store";

// Re-fork the drain loop this many times after an unexpected crash before
// giving up and locking the document.
const MAX_QUEUE_RESTARTS = 5;

// Thrown by the drain loop on a save error that cannot be retried (e.g.
// `unauthorized`); the supervisor locks immediately rather than re-forking. It
// carries the original `SaveError` so the lock is reported with its reason.
class UnrecoverableSaveError extends Error {
  constructor(readonly saveError: SaveError) {
    super(saveError.kind);
  }
}

/** Start Snapshot draining queue. */
export const snapshotQueueStarted = createAction(
  "authority/startSnapshotQueue"
);

/** Flush the queue immediately. */
export const snapshotQueueFlush = createAction("authority/flushSnapshotQueue");

/**
 * Drain the pending-snapshots queue on `snapshotQueueStarted`, saving each via
 * `saveBlockSnapshot` (BLOCK) or `saveFull` (FULL). Retryable network errors
 * back off and retry; failed snapshots are stored locally.
 *
 * Draining runs in a forked task under a supervisor loop: if the fork crashes
 * it re-forks (up to `MAX_QUEUE_RESTARTS`, since a reload would lose in-memory
 * pending snapshots), and locks the work on an unrecoverable error or once
 * restarts are exhausted.
 *
 * Using `cancelActiveListeners` keeps a single effect instance and
 * lets a later `snapshotQueueStarted` hand off to a fresh one.
 *
 * If there are no pending snapshots, the fork parks. The fork wakes from its
 * park on `snapshotQueueFlush`, `setPendingSnapshot`, or `onFocus`.
 */
export function startSnapshotQueueListener(startListening: AppStartListening) {
  startListening({
    actionCreator: snapshotQueueStarted,
    effect: async (_action, listenerApi) => {
      // Take over rather than `unsubscribe()`: unsubscribe is a one-way door,
      // once this effect exits nothing could restart the queue without a
      // reload. Re-sending an in-flight save on handoff is harmless: a version
      // the server already has resolves as a normal (fulfilled) save.
      listenerApi.cancelActiveListeners();

      // Supervisor: re-fork the drain loop if it crashes; lock the work on an
      // unrecoverable error or once restarts are exhausted.
      for (let attempt = 0; ; attempt++) {
        const task = listenerApi.fork(async (forkApi) => {
          for (;;) {
            // Park while there is nothing to drain.
            if (!selectHasPendingSnapshots(listenerApi.getState())) {
              await forkApi.pause(
                listenerApi.condition((action) => {
                  if (snapshotQueueFlush.match(action)) return true;
                  if (setPendingSnapshot.match(action)) return true;
                  if (onFocus.match(action)) return true;
                  return false;
                })
              );
            }

            const state = listenerApi.getState();
            const savingMechanism = selectSavingMechanism(state);

            // Dispatch all pending snapshots synchronously.
            for (const pending of state.authority.pendingSnapshots) {
              const saveResult = await saveSnapshot(
                pending,
                savingMechanism,
                listenerApi.dispatch
              );

              if (isRejected(saveResult)) {
                const saveError = saveResult.payload;

                // A rejection carrying a non-retryable SaveError payload
                // (currently only `unauthorized`) is unrecoverable: stop the
                // queue so the supervisor locks the work. Network errors arrive
                // thrown with no payload and fall through to the retry below.
                if (
                  saveError !== undefined &&
                  !isRetryableSaveError(saveError)
                ) {
                  throw new UnrecoverableSaveError(saveError);
                }

                // Retryable network error: store locally, back off, retry.
                storeSnapshotLocally(pending);
                await listenerApi.delay(3000);
                break;
              }
            }
          }
        });

        const result = await task.result;

        // Cancellation is the only clean stop (teardown). The loop runs
        // forever, so a plain return is as wrong as a throw: revive it.
        if (result.status === "cancelled") return;

        const error =
          result.status === "rejected"
            ? result.error
            : new Error("Snapshot drain loop exited unexpectedly");

        // Lock the work on an unrecoverable error or once restarts are spent.
        const documentError =
          error instanceof UnrecoverableSaveError ||
          attempt >= MAX_QUEUE_RESTARTS;

        // Report every queue death: `restart` (recoverable) vs `locked`
        // (terminal), with context to tell a crash from an unrecoverable error.
        const state = listenerApi.getState();
        const pendingSnapshots = selectPendingSnapshotsCount(state);
        const savesInFlight = selectSavesInFlightCount(state);

        captureException(error, {
          level: documentError ? "fatal" : "warning",
          tags: {
            snapshot_queue: documentError ? "locked" : "restart",
            snapshot_queue_exit: result.status,
          },
          extra: {
            attempt,
            maxRestarts: MAX_QUEUE_RESTARTS,
            unrecoverable: error instanceof UnrecoverableSaveError,
            saveError:
              error instanceof UnrecoverableSaveError
                ? error.saveError
                : undefined,
            pendingSnapshots,
            savesInFlight,
          },
        });

        if (documentError) {
          listenerApi.dispatch(setDocumentError());
          return;
        }
      }
    },
  });
}

/**
 * Save a pending snapshot either using the new BlockSnapshot mutation or the
 * old Full Save mutation for compatibility.
 *
 * @param pending - The pending snapshot to save
 * @param savingMechanism - BLOCK or FULL
 * @param dispatch - The dispatch function
 * @returns The result of the save action.
 */
async function saveSnapshot(
  pending: PendingSnapshot,
  savingMechanism: SavingMechanism,
  dispatch: AppDispatch
) {
  if (shouldSaveBlockSnapshot(savingMechanism, pending)) {
    return dispatch(saveBlockSnapshot(pending));
  } else {
    return dispatch(
      saveFull({
        pendingSnapshot: pending,
      })
    );
  }
}

function isClassicEditor(answerBlockId: string) {
  return (
    answerBlockId === ClassicEditorName.Body ||
    answerBlockId === ClassicEditorName.Notes ||
    answerBlockId === ClassicEditorName.References
  );
}

// Determines if SaveBlockSnapshot mutation should be called.
//
// SaveBlockSnapshot Mutation is used for MFA (which has BLOCK saving mechanism),
// but in MFA there is still Notes Editor(Classic one) that needs to be saved
// with SaveFull mutation.
function shouldSaveBlockSnapshot(
  savingMechanism: SavingMechanism,
  pending: PendingSnapshot
) {
  return (
    savingMechanism === SavingMechanism.BLOCK &&
    !isClassicEditor(pending.answerBlockId)
  );
}

function storeSnapshotLocally(pending: PendingSnapshot) {
  const storage = __GLOBAL_BROWSER_STORAGE.current;
  if (storage) {
    storage.storeSnapshot(pending);
  }
}

function selectHasPendingSnapshots(state: RootState): boolean {
  return state.authority.pendingSnapshots.length > 0;
}
